import React, { useEffect, useState } from 'react';
import Typography from '@mui/material/Typography';

import axios from 'src/axios';
// import { validateEmail } from 'src/util/util';
import { SnackContext } from 'src/store/ContextStore';
import { useFormContext } from 'src/components/custom-form/FormContext';
import NextButton from 'src/components/UI/button/NextButton';
import ConfirmationModal from 'src/components/UI/modals/ConfirmationModal';
import {
  FORM_TYPES,
  isHalfWidthFormType
} from 'src/components/form-builder/CreateQuestionnaire/constants/formTypes';

import { APICONSTANTS, CONSTANTS } from './Constants';

import { FormikProvider, useFormik } from 'formik';
import * as yup from 'yup';
import FormFields from './UserCustomFormFields.jsx';

import { jsPDF } from 'jspdf';
import { Box, Button, Modal } from '@mui/material';
import { Interweave } from 'interweave';
import previewStyles from '../form-builder/CreateQuestionnaire/components/Preview/Preview.module.css';
import ResponseHeaderPanel from '../form-builder/ResponseHeaderPanel';

export default function UserCustomForm({ readOnly, backLink }) {
  const { setSnack } = React.useContext(SnackContext);

  const {
    form: schema,
    responses,
    _id,
    token,
    signature,
    user,
    responseId,
    dispatchSubmitForm
  } = useFormContext();

  const [initialFormikValues, setInitialFormikValues] = useState(
    Object.keys(responses).length ? responses : {}
  );

  const [validationSchema, setValidationSchema] = useState(null);

  const [specificConditon, setSpecificConditon] = useState('');
  const [loading, setLoading] = useState(false);

  //setting render condition of starting node = true
  if (schema?.config?.schema?.[schema?.config?.startingGroupId]) {
    schema.config.schema[schema.config.startingGroupId].renderCondition = true;
  }

  useEffect(() => {
    if (!schema?.config?.schema) return;
    const yupValidationSchema = {};
    const initialValues = initialFormikValues;
    Object.keys(schema.config.schema).map((schemaId) => {
      let data = schema.config.schema[schemaId];
      (data.condition
        ? data.renderCondition === undefined
          ? true
          : data.renderCondition
        : true) &&
        data.formElements.map((element) => {
          //encoding label and replacing period  to resolve conflicting with formik
          let key = encodeURIComponent(element.label.replaceAll('.', '_'));

          if (element.isRequired) {
            if (
              element.type === FORM_TYPES.MULTIPLE_CHOICE ||
              element.type === FORM_TYPES.LINEAR_SCALE
            ) {
              yupValidationSchema[key] = yup
                .string()
                .required(`${element.label} is required`)
                .oneOf(element.options);
            } else if (element.type === FORM_TYPES.SHORT_TEXT) {
              yupValidationSchema[key] = yup
                .array()
                .min(1)
                .of(yup.string().required())
                .required();
            } else if (element.type === FORM_TYPES.CHECKBOX_INPUT) {
              yupValidationSchema[key] = yup
                .array()
                .of(
                  yup.object().shape({
                    checked: yup.boolean()
                  })
                )
                .test({
                  message: `${element.label} is required`,
                  test: (val) => !val.every((val) => val.checked === false)
                });
            } else {
              yupValidationSchema[key] = yup
                .string()
                .required(`${element.label} is required`);
            }
          }

          if (element.type === FORM_TYPES.CHECKBOX_INPUT) {
            const values = [];
            if (!initialValues[[key]]) {
              for (const option of element.options) {
                values.push({ label: option, checked: false, value: option });
              }
              if (element.isIdentifier) {
                values.push({ label: 'Other', checked: false, value: '' });
              }
            }
            initialValues[[key]] = initialValues[[key]] || [...values];
          } else if (element.type === FORM_TYPES.SHORT_TEXT) {
            initialValues[[key]] = initialValues[[key]] || [''];
          } else {
            initialValues[[key]] = initialValues[[key]] || '';
          }
        });
    });

    setInitialFormikValues(initialValues);
    setValidationSchema(yup.object(yupValidationSchema));
  }, [specificConditon]);

  const [openModal, setOpenModal] = React.useState(false);

  const [response, setResponse] = useState('');

  function saveFormDetails(values, shouldSubmit = false) {
    setLoading(true);

    const formData = new FormData();

    let responses = {};
    Object.keys(values).map((key) => {
      if (values[key] instanceof File) {
        formData.append(key, values[key]);
        responses[key] = values[key].name;
      } else {
        responses[key] = values[key];
      }
    });

    formData.append('responses', JSON.stringify(responses));
    formData.append('_id', _id);

    let method = axios.put;
    if (shouldSubmit) {
      method = axios.post;
    }

    const response = method(APICONSTANTS.schemaAndResponses, formData, {
      headers: {
        [CONSTANTS.authTokenKey]: token,
        'Content-Type': 'multipart/form-data'
      }
    })
      .then((res) => {
        if (shouldSubmit) dispatchSubmitForm(res?.data);
        else {
          setSnack({
            status: true,
            msg: 'Saved successfully!',
            severity: 'success'
          });
        }
      })
      .catch((err) => {
        // A failed upload saves nothing, and says so, so the patient knows to
        // try again rather than assume the file went through.
        const data = err?.response?.data;
        setSnack({
          status: true,
          msg:
            data?.code === 'FILE_UPLOAD_FAILED'
              ? data.error
              : 'An error has occurred!',
          severity: 'error'
        });
      })
      .finally(() => {
        setLoading(false);
      });

    setResponse(response);
  }

  const [shouldsubmit, setShouldSubmit] = React.useState(false);

  const formik = useFormik({
    initialValues: initialFormikValues,
    onSubmit: (values) => {
      shouldsubmit ? saveFormDetails(values, true) : saveFormDetails(values);
    },
    validationSchema: validationSchema
  });

  // Validate the main form BEFORE opening the confirmation modal. Previously the
  // modal opened unconditionally and validation only ran on the modal's Submit —
  // so required-field errors rendered hidden behind the modal and the button
  // appeared to do nothing. Now unmet rules surface inline on the form (mark the
  // errored fields touched) and the modal only opens when the form is valid.
  async function openSubmitModal() {
    const errors = await formik.validateForm();
    if (errors && Object.keys(errors).length > 0) {
      formik.setTouched(
        Object.keys(errors).reduce((acc, key) => ({ ...acc, [key]: true }), {}),
        false
      );
      return;
    }
    setOpenModal(true);
  }

  async function handleViewVcgsPdf() {
    setLoading(true);
    try {
      const { data } = await axios.get(`/form-builder/responses/${_id}/pdf`, {
        responseType: 'blob'
      });

      const blobUrl = URL.createObjectURL(
        new Blob([data], { type: 'application/pdf' })
      );
      window.open(blobUrl, '_blank');
    } catch (err) {
      setSnack({
        status: true,
        msg: 'Failed to generate VCGS PDF',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!schema?.config?.schema) return;
    {
      Object.keys(schema.config.schema).map((schemaId) => {
        let formData = schema.config.schema[schemaId];

        const key = formData.condition?.condition.split('| ')[1];
        const value = formData.condition?.value;
        const formikValue = formik.values[key];

        const previousFormRenderCondition =
          formData.previousConnections
            .map((id) => {
              return schema.config.schema[id]?.renderCondition;
            })
            .indexOf(true) > -1
            ? true
            : false;

        function arrayIncludesAll(required, answer) {
          const checked = [];
          for (const item of answer) {
            if (item.checked) {
              checked.push(item.value);
            }
          }
          return (
            Array.isArray(required) &&
            required.length > 0 &&
            required.every((val) => checked.includes(val))
          );
        }

        let condition;
        if (
          key &&
          value &&
          Array.isArray(value) &&
          Array.isArray(formikValue)
        ) {
          condition =
            arrayIncludesAll(value, formikValue) && previousFormRenderCondition;
        } else {
          if (key)
            condition =
              formik.values[encodeURIComponent(key)] === value &&
              previousFormRenderCondition;
        }

        if (formData.condition) {
          setSpecificConditon(condition === undefined ? true : condition);
          schema.config.schema[schemaId].renderCondition =
            condition === undefined ? true : condition;
        } else {
          setSpecificConditon(false);
        }
      });
    }
  }, [formik.values]);

  // This will initialise form with initialFormikValues
  useEffect(() => {
    formik.setValues(initialFormikValues);
  }, [initialFormikValues]);

  const submitForm = (submit = false) => {
    formik.handleSubmit();
    if (submit) {
      setShouldSubmit(true);
      if (response) {
        return response;
      }
    }
  };

  const generatePdf = () => {
    const doc = new jsPDF('p', 'pt', 'a4');
    doc.html(document.querySelector('#content'), {
      margin: [40, 60, 40, 40],
      callback: function (pdf) {
        pdf.save('doc.pdf');
      }
    });
  };
  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const style = {
    position: 'absolute',
    marginInline: 10,
    width: '30%',
    minWidth: 500,
    height: '80%',
    overflowY: 'scroll',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    bgcolor: 'background.paper',
    boxShadow: 24,
    display: 'flex',
    // justifyContent: 'center',
    paddingBlock: '1rem',
    paddingInline: '2rem'
  };

  if (!schema?.config?.schema) return null;

  let signatureFieldLabel = '';
  const hasSignatureField = Object.values(schema.config.schema).some((group) =>
    group.formElements?.some((el) => {
      if (el.type === FORM_TYPES.SIGNATURE) {
        signatureFieldLabel = el.label;
        return true;
      }
      return false;
    })
  );
  const isSigned = Object.values(formik.values).some(
    (v) => typeof v === 'string' && v.startsWith('data:image/')
  );

  const schemaMap = schema.config.schema;

  const getChildren = (node) =>
    (node.groupsConnectedTo || [])
      .map((child) => (child && child.id ? schemaMap[child.id] : null))
      .filter(Boolean);

  const isVisible = (node) => (node.condition ? node.renderCondition : true);

  const renderFields = (node) => (
    <FormikProvider value={formik}>
      <div className={previewStyles.fieldsGrid}>
        {node.formElements.map((element, idx) => (
          <div
            key={idx}
            className={
              isHalfWidthFormType(element.type)
                ? previewStyles.fieldCellHalf
                : previewStyles.fieldCellFull
            }
          >
            <FormFields element={element} formik={formik} readOnly={readOnly} />
          </div>
        ))}
      </div>
    </FormikProvider>
  );

  const renderNode = (node, visited) => {
    if (!node || visited.has(node.id) || !isVisible(node)) return null;
    visited.add(node.id);
    const isForm = node.formType !== false;
    const children = getChildren(node);

    if (isForm) {
      return (
        <div key={node.id} className={previewStyles.subFormBlock}>
          <div className={previewStyles.subFormHeader}>
            <h2 className={previewStyles.subFormTitle}>{node.title}</h2>
            {node.description && (
              <p className={previewStyles.formDescription}>
                {node.description}
              </p>
            )}
          </div>
          {renderFields(node)}
          {children.length > 0 && (
            <div className={previewStyles.subFormBody}>
              {children.map((child) => renderNode(child, visited))}
            </div>
          )}
        </div>
      );
    }

    return (
      <React.Fragment key={node.id}>
        <div className={previewStyles.sectionCard}>
          <div className={previewStyles.sectionCardHeader}>
            <h3 className={previewStyles.sectionCardTitle}>{node.title}</h3>
            {node.description && (
              <p className={previewStyles.sectionCardDesc}>
                {node.description}
              </p>
            )}
          </div>
          {renderFields(node)}
        </div>
        {children.map((child) => renderNode(child, visited))}
      </React.Fragment>
    );
  };

  const rootNode = schemaMap[schema.config.startingGroupId];
  const treeVisited = new Set();
  let rootChildren = [];
  if (rootNode) {
    treeVisited.add(rootNode.id);
    rootChildren = getChildren(rootNode);
  }

  return (
    <>
      <div>
        {readOnly && (
          <ResponseHeaderPanel
            backLink={backLink}
            responseId={responseId}
            user={user}
            submittedAt={signature?.signedAt}
            onGeneratePdf={handleOpen}
            onViewVcgs={handleViewVcgsPdf}
            showVcgs={schema?.config?.postActions === '1'}
            vcgsLoading={loading}
          />
        )}
        <Modal open={open} onClose={handleClose}>
          <Box sx={style}>
            <div style={{ minWidth: 400, width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                  onClick={generatePdf}
                  color="secondary"
                  variant="contained"
                >
                  download
                </Button>
              </div>
              <div
                style={{
                  paddingBlock: 20
                }}
                id="content"
              >
                <Typography variant="h4" className="primary-color" gutterBottom>
                  {schema.title}
                </Typography>
                <Typography gutterBottom>{schema.description}</Typography>

                {Object.keys(formik.values).map((val) => {
                  const raw = formik.values[val];
                  if (
                    typeof raw === 'string' &&
                    raw.startsWith('data:image/')
                  ) {
                    return null;
                  }

                  let data = raw.toString();
                  if (typeof raw === 'object') {
                    // The file's name only. This block is what "download"
                    // turns into a PDF, a document kept long after any signed
                    // link has expired — and `key` is a storage name, not a
                    // link at all. The form above links to the file.
                    if (raw?.type === 'file') data = raw.name;
                    if (typeof raw[0] === 'object') {
                      let items = [];
                      for (const item of raw) {
                        if (item.checked) items.push(item.value);
                      }
                      data = items.toString();
                      console.log(data);
                    }
                  }
                  return (
                    <div
                      key={val}
                      style={{ width: '100%', marginRight: '1rem' }}
                    >
                      <div
                        style={{
                          marginRight: '1rem'
                        }}
                      >
                        <div
                          style={{
                            fontSize: '1rem',
                            color: 'black',
                            fontWeight: 'bold',
                            marginRight: '1rem',
                            paddingBlock: '25px'
                          }}
                        >
                          <Interweave
                            content={decodeURIComponent(
                              val.replaceAll('_', '.')
                            )}
                          />
                        </div>
                        <div />
                        {data}
                      </div>
                    </div>
                  );
                })}
                {signature?.signedBy &&
                  Object.values(formik.values).some(
                    (v) => typeof v === 'string' && v.startsWith('data:image/')
                  ) && (
                    <div
                      style={{
                        width: '100%',
                        marginRight: '1rem',
                        paddingBlock: '25px'
                      }}
                    >
                      <div style={{ marginTop: 8 }}>
                        <div>
                          <strong>Signed By:</strong> {signature.signedBy}
                        </div>
                        {signature.signedAt && (
                          <div>
                            <strong>Signed On:</strong>{' '}
                            {new Date(signature.signedAt).toLocaleString()}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
              </div>
            </div>
          </Box>
        </Modal>

        {rootNode && isVisible(rootNode) && (
          <div
            className={previewStyles.previewContainer}
            style={{ marginBlock: 20 }}
          >
            <div className={previewStyles.formHeader}>
              <h2 className={previewStyles.formTitle}>{rootNode.title}</h2>
              {rootNode.description && (
                <p className={previewStyles.formDescription}>
                  {rootNode.description}
                </p>
              )}
            </div>
            {renderFields(rootNode)}
            {rootChildren.map((child) => renderNode(child, treeVisited))}

            {readOnly && hasSignatureField && (
              <div style={{ marginTop: 16 }}>
                {isSigned && signature?.signedBy ? (
                  <>
                    <Typography variant="body2">
                      <strong>Signed By:</strong> {signature.signedBy}
                    </Typography>
                    {signature.signedAt && (
                      <Typography variant="body2">
                        <strong>Signed On:</strong>{' '}
                        {new Date(signature.signedAt).toLocaleString()}
                      </Typography>
                    )}
                  </>
                ) : (
                  <>
                    <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                      {signatureFieldLabel}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary'
                      }}
                    >
                      —
                    </Typography>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {!readOnly && (
          <NextButton
            label="Submit"
            onClick={openSubmitModal}
            onSaveClick={() => submitForm()}
            centerActions
          />
        )}
        {!readOnly && (
          <ConfirmationModal
            openModal={openModal}
            setOpenModal={setOpenModal}
            onSaveClick={() => submitForm(true)}
            isParentHandlingLoading={true}
            isParentLoading={loading}
          />
        )}
      </div>
    </>
  );
}
